// @flow

import React, {useState} from 'react';
import * as Sentry from '@sentry/react';
import {useFrame} from '@stripe-internal/stripethirdparty';
import type {YouTubePlayerOutboundPayload} from '@stripe-internal/stripethirdparty/lib/shared/YouTubePlayerTypes';
import {useAnalytics} from 'docs/data';
import type {VideoModel} from 'docs/data';

const frameProps = {
  width: '100%',
  height: '100%',
  allow: 'autoplay; fullscreen',
};

type VideoPlayerHookProps = {
  video: VideoModel,
  onError?: (error: any) => void,
};

type VideoPlayerHook = {
  frame: any,
  isPlaying: boolean,
  isLoaded: boolean,
  playVideo: () => void,
  pauseVideo: () => void,
  requestCurrentTime: () => void,
  currentTime: number,
  loadNewVideo: (youtubeId: string, startSeconds?: number) => void,
  hasCompletedPlayback: boolean,
};

// This wraps the stripethirdparty `useFrame` with the YouTubePlayer.
// NOTE: You cannot call this hook during SSR, you should use `<OnlyRenderOnClient>`
// to ensure this component will only be called on the browser.
export const useVideoPlayer = (
  props: VideoPlayerHookProps,
): VideoPlayerHook => {
  const {trackAction} = useAnalytics();

  const youtubeVideoId = props.video.youtubeId;

  const [hasCompletedPlayback, setHasCompletedPlayback] = useState(false);
  const [hasInitiatedPlayback, setHasInitiatedPlayback] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const handleMessage = (msg: YouTubePlayerOutboundPayload) => {
    if (msg.name === 'events.played') {
      setPlaying(true);

      if (!hasInitiatedPlayback || hasCompletedPlayback) {
        setHasInitiatedPlayback(true);
        setHasCompletedPlayback(false);
        trackAction('docs_videos_playback_started', {
          youtubeVideoId,
        });
      }
    } else if (msg.name === 'events.paused') {
      setPlaying(false);

      if (msg.completed) {
        setHasCompletedPlayback(true);
        trackAction('docs_videos_playback_completed', {
          youtubeVideoId,
        });
      }
    } else if (msg.name === 'events.loaded') {
      setLoaded(true);
      trackAction('docs_videos_loaded', {
        youtubeVideoId,
      });
    } else if (msg.name === 'events.getTime') {
      setCurrentTime(msg.timestamp);
    } else if (msg.name === 'events.error') {
      if (Sentry && Sentry.captureException) {
        const {name, ...event} = msg;
        Sentry.captureException({
          name: 'VideoPlayerError',
          ...event,
        });
      }

      props.onError && props.onError(msg);
    }
  };

  const handleError = (frameError) => {
    if (Sentry && Sentry.captureException) {
      Sentry.captureException(frameError);
    }
  };

  const onReady = (sendMessage) => {
    sendMessage({
      name: 'actions.load',
      youtubeVideoId,
      options: {
        controls: true,
      },
    });
  };

  const {frame, sendMessage, sendRequest} = useFrame({
    // TODO: make this a server variable
    baseURL: 'https://b.stripecdn.com/stripethirdparty-srv/assets/',
    type: 'YouTubePlayer',
    handleMessage,
    handleError,
    onReady,
    frameProps,
  });

  const playVideo = React.useCallback(() => {
    if (!isLoaded) {
      return;
    }

    sendMessage({
      name: 'actions.play',
    });
  }, [sendMessage, isLoaded]);

  const pauseVideo = React.useCallback(() => {
    if (!isLoaded) {
      return;
    }

    sendMessage({
      name: 'actions.pause',
    });
  }, [sendMessage, isLoaded]);

  const requestCurrentTime = React.useCallback(() => {
    if (!isLoaded) {
      return;
    }

    sendRequest({
      name: 'actions.getTime',
    });
  }, [sendRequest, isLoaded]);

  const loadNewVideo = React.useCallback(
    (youtubeId: string, startSeconds?: number) => {
      setPlaying(false);
      sendMessage({
        name: 'actions.loadNewVideo',
        youtubeVideoId: youtubeId,
        options: {
          controls: true,
          startSeconds,
        },
      });
    },
    [sendMessage],
  );

  return {
    frame,
    isPlaying,
    isLoaded,
    currentTime,
    playVideo,
    pauseVideo,
    requestCurrentTime,
    loadNewVideo,
    hasCompletedPlayback,
  };
};
